import { useState, useEffect } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetAuthApi, usePostAuthApi } from '../../../../hooks/UseApiCalls';
import { useQueryClient } from '@tanstack/react-query';
import useAuthContext from '../../../../contexts/Auth/useAuthContext';
import { postAuth, putAuth, delAuth } from '../../../../api/ApiCalls';
import { DepartmentDtoIn } from '../../../../models/in/DepartmentDtoIn';
import Button from '../../../../components/ui/Button';
import styles from './InquiriesSettingsPage.module.scss';

interface SystemSetting {
  key: string;
  value: string;
  valueType: string;
  description: string;
}

interface User {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

export default function InquiriesSettingsPage() {
  const { t } = useTranslation();
  const { tab } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null);
  /** מפתחות שהשרת לא מכיר - התקנה שהמיגרציה שלה עוד לא רצה. ראה saveAll. */
  const [missingKeys, setMissingKeys] = useState<string[]>([]);

  const { data: serverSettings } = useGetAuthApi<SystemSetting[]>('management/settings');

  useEffect(() => {
    if (serverSettings) {
      const map: Record<string, string> = {};
      serverSettings.forEach(s => { map[s.key] = s.value; });
      setSettings(map);
    }
  }, [serverSettings]);

  const updateSetting = (key: string, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaveStatus(null);
  };

  const saveAll = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    try {
      const edited = Object.entries(settings).filter(([key, value]) => {
        const serverSetting = serverSettings?.find(s => s.key === key);
        return !serverSetting || serverSetting.value !== value;
      });

      /*
       * ⚠️ A setting the server does not have cannot be saved - the API rejects an unknown key,
       * since the rows are created by a migration - and this used to drop those silently and
       * still report success. Someone typed the office email address into an installation whose
       * migration had not run, was told it saved, and got no mail with nothing on screen to
       * explain it. Now it says which key, so the answer is "that installation is behind" rather
       * than "the emails do not work".
       */
      const unknownKeys = edited.filter(([key]) => !serverSettings?.some(s => s.key === key));
      if (unknownKeys.length > 0) {
        setMissingKeys(unknownKeys.map(([key]) => key));
        setSaveStatus('error');
        setIsSaving(false);
        return;
      }

      setMissingKeys([]);
      for (const [key, value] of edited) {
        await putAuth(`management/settings/${key}`, headers, {}, { value });
      }
      queryClient.invalidateQueries({ queryKey: ['management/settings'] });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch {
      setSaveStatus('error');
    }
    setIsSaving(false);
  };

  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  const { data: departments, refetch: refetchDepts } = useGetAuthApi<DepartmentDtoIn[]>('management/departments');
  const { data: users } = useGetAuthApi<User[]>('users');
  const [newDeptName, setNewDeptName] = useState('');
  const [addUserDeptId, setAddUserDeptId] = useState<number | null>(null);
  const [addUserEmail, setAddUserEmail] = useState('');

  const createDepartment = usePostAuthApi<DepartmentDtoIn>('management/departments', {
    onSuccess: () => { refetchDepts(); setNewDeptName(''); },
  });

  const handleDeleteDept = async (deptId: number) => {
    try {
      await delAuth(`management/departments/${deptId}`, headers);
      refetchDepts();
    } catch {
      setSaveStatus('error');
    }
  };

  const handleAddUser = async (deptId: number, email: string) => {
    try {
      await postAuth('management/departments/' + deptId + '/users', headers, { email });
      refetchDepts();
      setAddUserDeptId(null);
      setAddUserEmail('');
    } catch {
      setSaveStatus('error');
    }
  };

  const handleRemoveUser = async (deptId: number, email: string) => {
    try {
      await delAuth(`management/departments/${deptId}/users/${email}`, headers);
      refetchDepts();
    } catch {
      setSaveStatus('error');
    }
  };

  const adminUsers = users?.filter(u => u.roles?.includes('Admin')) || [];

  const currentTab = tab || 'auto-close';
  const tabItems = [
    { id: 'auto-close', label: t('inquiries.settings.autoClose.title') },
    { id: 'departments', label: t('inquiries.settings.departments.title') },
    { id: 'email', label: t('inquiries.settings.email.title') },
    { id: 'files', label: t('inquiries.settings.files.title') },
    { id: 'untreated', label: t('inquiries.settings.untreated.title') },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <button
          className={styles.backBtn}
          onClick={() => navigate('/management/inquiries')}
          title={t('inquiries.settings.backToInquiries')}
        >
          <ArrowForwardIcon sx={{ fontSize: 20 }} />
        </button>
        <h2 className="heading-h3-semibold">{t('inquiries.settings.title')}</h2>
      </div>

      <div className={styles.tabs}>
        {tabItems.map((ti) => (
          <button
            key={ti.id}
            className={currentTab === ti.id ? styles.tabActive : styles.tab}
            onClick={() => navigate(`/management/settings/inquiries/${ti.id}`)}
          >
            {ti.label}
          </button>
        ))}
      </div>

      {currentTab === 'auto-close' && (
        <div className={styles.card}>
          <h3 className={`heading-h4-semibold ${styles.cardTitle}`}>
            {t('inquiries.settings.autoClose.title')}
          </h3>
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={settings['AutoClose.Enabled'] === 'true'}
              onChange={(e) => updateSetting('AutoClose.Enabled', e.target.checked.toString())}
            />
            <span className="body-sm-regular">{t('inquiries.settings.autoClose.enable')}</span>
          </label>
          <div className={styles.settingRow}>
            <span className="body-sm-regular">{t('inquiries.settings.autoClose.threshold')}</span>
            <input
              type="number"
              className={styles.numberInput}
              value={settings['AutoClose.ThresholdHours'] || ''}
              onChange={(e) => updateSetting('AutoClose.ThresholdHours', e.target.value)}
            />
            <span className="body-sm-regular">{t('inquiries.settings.autoClose.hours')}</span>
          </div>
        </div>
      )}

      {currentTab === 'departments' && (
        <div className={styles.card}>
          <h3 className={`heading-h4-semibold ${styles.cardTitle}`}>
            {t('inquiries.settings.departments.title')}
          </h3>
          <p className={`body-sm-regular ${styles.deptDescription}`}>
            {t('inquiries.settings.departments.description')}
          </p>

          <div className={styles.deptCreateRow}>
            <input
              type="text"
              className={styles.textInput}
              placeholder={t('inquiries.settings.departments.namePlaceholder')}
              value={newDeptName}
              onChange={(e) => setNewDeptName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newDeptName.trim()) {
                  createDepartment.mutate({ name: newDeptName.trim() } as never);
                }
              }}
            />
            <Button
              variant="primary"
              onClick={() => createDepartment.mutate({ name: newDeptName.trim() } as never)}
              disabled={!newDeptName.trim() || createDepartment.isPending}
            >
              <AddIcon sx={{ fontSize: 18, marginInlineEnd: '4px' }} />
              {t('inquiries.settings.departments.add')}
            </Button>
          </div>

          {departments && departments.length > 0 ? (
            <div className={styles.deptList}>
              {departments.map((dept) => (
                <div key={dept.id} className={styles.deptCard}>
                  <div className={styles.deptHeader}>
                    <span className="body-sm-semibold">{dept.name}</span>
                    <div className={styles.deptHeaderActions}>
                      <span className="body-xs-sm-regular" style={{ color: 'var(--font-body-2)' }}>
                        {dept.openInquiryCount} {t('inquiries.settings.departments.openInquiries')}
                      </span>
                      <button className={styles.deptDeleteBtn} onClick={() => handleDeleteDept(dept.id)} title={t('inquiries.settings.departments.delete')}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </div>

                  {dept.users.length > 0 && (
                    <ul className={styles.deptUsers}>
                      {dept.users.map((u) => (
                        <li key={u.email} className={styles.deptUser}>
                          <span className="body-sm-regular">{u.firstName} {u.lastName}</span>
                          <span className="body-xs-sm-regular" style={{ color: 'var(--font-body-2)' }}>{u.email}</span>
                          <button className={styles.deptRemoveUserBtn} onClick={() => handleRemoveUser(dept.id, u.email)} title={t('inquiries.settings.departments.removeUser')}>
                            <PersonRemoveIcon sx={{ fontSize: 14 }} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {addUserDeptId === dept.id ? (
                    <div className={styles.deptAddUserRow}>
                      <select
                        className={styles.textInput}
                        value={addUserEmail}
                        onChange={(e) => setAddUserEmail(e.target.value)}
                      >
                        <option value="">{t('inquiries.settings.departments.selectUser')}</option>
                        {adminUsers.filter(u => !dept.users.some(du => du.email === u.email)).map(u => (
                          <option key={u.email} value={u.email}>{u.firstName} {u.lastName}</option>
                        ))}
                      </select>
                      <Button variant="outline" onClick={() => { if (addUserEmail) handleAddUser(dept.id, addUserEmail); }} disabled={!addUserEmail}>
                        {t('inquiries.settings.departments.addUser')}
                      </Button>
                      <button className={styles.deptDeleteBtn} onClick={() => { setAddUserDeptId(null); setAddUserEmail(''); }}>✕</button>
                    </div>
                  ) : (
                    <button className={styles.deptAddUserTrigger} onClick={() => setAddUserDeptId(dept.id)}>
                      <PersonAddIcon sx={{ fontSize: 14 }} />
                      <span>{t('inquiries.settings.departments.addUser')}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="body-sm-regular" style={{ color: 'var(--font-body-2)', marginTop: '12px' }}>
              {t('inquiries.settings.departments.empty')}
            </p>
          )}
        </div>
      )}

      {currentTab === 'email' && (
        <div className={styles.card}>
          <h3 className={`heading-h4-semibold ${styles.cardTitle}`}>
            {t('inquiries.settings.email.title')}
          </h3>
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={settings['Email.NotifyOnReply'] === 'true'}
              onChange={(e) => updateSetting('Email.NotifyOnReply', e.target.checked.toString())}
            />
            <span className="body-sm-regular">{t('inquiries.settings.email.notifyOnReply')}</span>
          </label>
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={settings['Email.IncludeContent'] === 'true'}
              onChange={(e) => updateSetting('Email.IncludeContent', e.target.checked.toString())}
            />
            <span className="body-sm-regular">{t('inquiries.settings.email.includeContent')}</span>
          </label>
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={settings['Email.NotifyOfficeOnUserMessage'] === 'true'}
              onChange={(e) => updateSetting('Email.NotifyOfficeOnUserMessage', e.target.checked.toString())}
            />
            <span className="body-sm-regular">{t('inquiries.settings.email.notifyOfficeOnUserMessage')}</span>
          </label>
          <div className={styles.settingRow}>
            <span className="body-sm-regular">{t('inquiries.settings.email.officeAddress')}</span>
            <input
              type="email"
              className={styles.textInput}
              placeholder={t('inquiries.settings.email.officeAddressPlaceholder')}
              value={settings['Email.OfficeAddress'] ?? ''}
              onChange={(e) => updateSetting('Email.OfficeAddress', e.target.value)}
            />
          </div>
          {/* Says out loud that the switch alone does nothing - the likeliest way to get this wrong. */}
          <p className="body-xs-sm-regular" style={{ color: 'var(--font-body-2)' }}>
            {t('inquiries.settings.email.officeAddressHint')}
          </p>
        </div>
      )}

      {currentTab === 'files' && (
        <div className={styles.card}>
          <h3 className={`heading-h4-semibold ${styles.cardTitle}`}>
            {t('inquiries.settings.files.title')}
          </h3>
          <div className={styles.settingRow}>
            <span className="body-sm-regular">{t('inquiries.settings.files.maxSize')}</span>
            <input
              type="number"
              className={styles.numberInput}
              value={settings['Files.MaxSizeMB'] || ''}
              onChange={(e) => updateSetting('Files.MaxSizeMB', e.target.value)}
            />
            <span className="body-sm-regular">MB</span>
          </div>
          <div className={styles.settingRow}>
            <span className="body-sm-regular">{t('inquiries.settings.files.maxPerInquiry')}</span>
            <input
              type="number"
              className={styles.numberInput}
              value={settings['Files.MaxPerInquiry'] || ''}
              onChange={(e) => updateSetting('Files.MaxPerInquiry', e.target.value)}
            />
          </div>
        </div>
      )}

      {currentTab === 'untreated' && (
        <div className={styles.card}>
          <h3 className={`heading-h4-semibold ${styles.cardTitle}`}>
            {t('inquiries.settings.untreated.title')}
          </h3>
          <div className={styles.settingRow}>
            <span className="body-sm-regular">{t('inquiries.settings.untreated.threshold')}</span>
            <input
              type="number"
              className={styles.numberInput}
              value={settings['Untreated.ThresholdHours'] || ''}
              onChange={(e) => updateSetting('Untreated.ThresholdHours', e.target.value)}
            />
            <span className="body-sm-regular">{t('inquiries.settings.autoClose.hours')}</span>
          </div>
        </div>
      )}

      {currentTab !== 'departments' && (
        <div className={styles.footer}>
          {saveStatus === 'success' && (
            <span className={styles.saveSuccess}>
              <CheckCircleIcon sx={{ fontSize: 16 }} />
              {t('inquiries.settings.saveSuccess')}
            </span>
          )}
          {saveStatus === 'error' && (
            <span className={styles.saveError}>
              <ErrorIcon sx={{ fontSize: 16 }} />
              {/* אומר איזה מפתח חסר, כדי שהתשובה תהיה "ההתקנה מאחור" ולא "השמירה נכשלה" */}
              {missingKeys.length > 0
                ? t('inquiries.settings.saveMissingKeys', { keys: missingKeys.join(', ') })
                : t('inquiries.settings.saveError')}
            </span>
          )}
          <Button variant="primary" onClick={saveAll} disabled={isSaving}>
            <SaveIcon sx={{ fontSize: 18, marginInlineEnd: '4px' }} />
            {isSaving ? t('inquiries.settings.saving') : t('inquiries.settings.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
